/**
 * Fullscreen utility functions with proper error handling
 * Browsers require user interaction before allowing fullscreen mode
 */

// Import toast for user notifications (if available)
let showToast: ((message: string, type?: 'success' | 'error' | 'info') => void) | null = null;

// Try to import toast if it's available
try {
  const toastModule = require('react-hot-toast');
  showToast = toastModule.default || toastModule.toast;
} catch (error) {
  // Toast not available, fallback to console
  showToast = (message: string, type?: 'success' | 'error' | 'info') => {
    // Toast notification
  };
}

export const enterFullscreen = async (): Promise<boolean> => {
  try {
    if (document.documentElement.requestFullscreen) {
      await document.documentElement.requestFullscreen();
    } else if ((document.documentElement as any).webkitRequestFullscreen) {
      await (document.documentElement as any).webkitRequestFullscreen();
    } else if ((document.documentElement as any).msRequestFullscreen) {
      await (document.documentElement as any).msRequestFullscreen();
    } else {
      const message = 'Fullscreen not supported in this browser';
      showToast?.(message, 'error');
      return false;
    }
    
    showToast?.('Fullscreen mode activated', 'success');
    return true;
  } catch (error: any) {
    
    // Provide user-friendly error messages
    let userMessage = 'Fullscreen request failed';
    
    if (error.name === 'NotAllowedError') {
      userMessage = 'Fullscreen denied: Please click the fullscreen button or press F11';
    } else if (error.name === 'TypeError') {
      userMessage = 'Fullscreen not supported in this browser';
    } else if (error.name === 'SecurityError') {
      userMessage = 'Fullscreen blocked due to security restrictions';
    }
    
    showToast?.(userMessage, 'error');
    return false;
  }
};

export const exitFullscreen = async (): Promise<boolean> => {
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      await (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      await (document as any).msExitFullscreen();
    } else {
      return false;
    }
    return true;
  } catch (error: any) {
    return false;
  }
};

export const toggleFullscreen = async (): Promise<boolean> => {
  const isFullscreen = !!(document.fullscreenElement || 
                          (document as any).webkitFullscreenElement || 
                          (document as any).msFullscreenElement);
  
  if (isFullscreen) {
    return await exitFullscreen();
  } else {
    return await enterFullscreen();
  }
};

export const isFullscreenSupported = (): boolean => {
  return !!(
    document.documentElement.requestFullscreen ||
    (document.documentElement as any).webkitRequestFullscreen ||
    (document.documentElement as any).msRequestFullscreen
  );
};

export const getFullscreenElement = (): Element | null => {
  return document.fullscreenElement || 
         (document as any).webkitFullscreenElement || 
         (document as any).msFullscreenElement;
};

export const isInFullscreen = (): boolean => {
  return !!getFullscreenElement();
};
